// In-match HUD components (plan §25). Reads Game/Sim state directly (read-only by convention).
// Visual pass 4: ornate fantasy chrome matching the owner's UI mockup.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Game } from '../app/game';
import { FACTIONS } from '../content/factions';
import { buildingDef, cardDef, defs, spellDef, unitDef } from '../content/registry';
import { TUNING } from '../content/tuning';
import type { UpgradeDef } from '../content/types';
import { cardUiState } from '../sim/systems/cards';
import { idleWorkerCount } from '../sim/systems/workers';
import type { Building, EntId, HLKind } from '../sim/state';
import { squadMembers } from '../sim/state';
import { dist } from '../shared/vec';
import { CardFace } from './cards';

function fmtTime(t: number): string {
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const ROMAN = ['I', 'II', 'III', 'IV'];
const PLAYER_COLORS = ['#3b82f6', '#38bdf8', '#f59e0b', '#f97316'] as const;

// ---------------------------------------------------------------- top bar

/** Rolling resource income (per 10 s), sampled from cumulative gather stats. */
function useIncome(game: Game): { power: number; essence: number } {
  const ref = useRef({ t: -1, pow: 0, ess: 0, rp: 0, re: 0 });
  const st = game.sim.state;
  const p = st.players[game.localPlayer];
  const r = ref.current;
  if (r.t < 0) {
    r.t = st.time;
    r.pow = p.stats.powerGathered;
    r.ess = p.stats.essenceGathered;
  } else if (st.time - r.t >= 3) {
    const span = st.time - r.t;
    r.rp = ((p.stats.powerGathered - r.pow) / span) * 10;
    r.re = ((p.stats.essenceGathered - r.ess) / span) * 10;
    r.t = st.time;
    r.pow = p.stats.powerGathered;
    r.ess = p.stats.essenceGathered;
  }
  return { power: Math.round(r.rp), essence: Math.round(r.re) };
}

function AscensionPips({ playable, discovered }: { playable: number; discovered: number }) {
  return (
    <span
      className="stat"
      title={`Ascension ${ROMAN[playable - 1]} — control sites to ascend (playable ${ROMAN[playable - 1]}, discovered ${ROMAN[discovered - 1]})`}
    >
      <span className="ascglyph">▲</span>
      <span className="ascpips">
        {[1, 2, 3, 4].map((tier) => (
          <i key={tier} className={tier <= playable ? 'on' : tier <= discovered ? 'seen' : ''} />
        ))}
      </span>
      <b className="ascroman">{ROMAN[playable - 1]}</b>
    </span>
  );
}

export function TopBar({ game, onOpenMenu }: { game: Game; onOpenMenu?: () => void }) {
  const st = game.sim.state;
  const p = st.players[game.localPlayer];
  const income = useIncome(game);
  const wfClass = p.workforceUsed >= p.workforceCap ? 'bad' : p.workforceUsed >= p.workforceCap * 0.9 ? 'warn' : '';
  const cmdClass = p.commandUsed >= p.commandCap ? 'bad' : p.commandUsed >= p.commandCap * 0.9 ? 'warn' : '';
  const sites0 = game.sim.sitesOwned(game.localPlayer);
  return (
    <div className="topbar">
      <span className="stat res" title="Power — worker-gathered energy">
        <i className="icon power">⚡</i> <b>{Math.floor(p.power)}</b>
        {income.power > 0 && <em className="rate">+{income.power}</em>}
      </span>
      <span className="stat res" title="Essence — rarer arcane resource">
        <i className="icon essence">✦</i> <b>{Math.floor(p.essence)}</b>
        {income.essence > 0 && <em className="rate">+{income.essence}</em>}
      </span>
      <span className={`stat ${wfClass}`} title={`Workforce ${p.workforceUsed}/${p.workforceCap} (${FACTIONS[p.faction].workersPerZone} per zone: Core territory + each controlled site)`}>
        <i className="icon">👷</i> {p.workforceUsed}/{p.workforceCap}
      </span>
      <span className={`stat ${cmdClass}`} title={`Command ${p.commandUsed}/${p.commandCap} (Core ${TUNING.commandCore} + sites ×${TUNING.commandPerSite} + Ascension + Command Hall)`}>
        <i className="icon">⚔</i> {p.commandUsed}/{p.commandCap}
      </span>
      <AscensionPips playable={p.ascensionPlayable} discovered={p.ascensionDiscovered} />
      <span className="stat" title="Strategic sites owned">
        <i className="icon sites">◆</i> {sites0}/{game.sim.map.sites.length}
      </span>
      {p.faction === 'ashen-host' && (
        <span className="stat" title="Corpse Marks: stored at the Throne / lying on the field">
          <i className="icon">💀</i> {p.corpseStored}/{st.corpseMarks.length}
        </span>
      )}
      <span className="stat" title="Match time">
        <i className="icon">⏱</i> {fmtTime(st.time)}
      </span>
      <span style={{ flex: 1 }} />
      {game.settings.showFps && <span className="stat fps">{Math.round(game.fps)} fps</span>}
      {game.onlineRuntime && <span className={`netstatus ${game.onlineRuntime.status}`}>{game.onlineRuntime.status}</span>}
      <button className="iconbtn" disabled={Boolean(game.onlineRuntime)} title={game.onlineRuntime ? 'Pause is disabled in online matches' : st.paused ? 'Resume (Space)' : 'Pause (Space)'} onClick={() => game.togglePause()}>
        {st.paused ? '▶' : '⏸'}
      </button>
      <button className="iconbtn" title="Menu" onClick={onOpenMenu}>
        ☰
      </button>
    </div>
  );
}

// ---------------------------------------------------------------- card bar

/** Deck-slot hotkeys (GDD §17.1): one row, 1–9 then 0 */
export const DECK_HOTKEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

export function CardBar({ game }: { game: Game }) {
  const [hover, setHover] = useState<string | null>(null);
  const hoverTimerRef = useRef<number | null>(null);
  const p = game.sim.state.players[game.localPlayer];
  const rows = [p.deck]; // GDD §17.1: a single visible row of 10
  const cancelHover = (cardId?: string) => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (cardId) setHover((current) => (current === cardId ? null : current));
  };
  const startHover = (cardId: string) => {
    cancelHover();
    setHover(null);
    hoverTimerRef.current = window.setTimeout(() => {
      setHover(cardId);
      hoverTimerRef.current = null;
    }, 1000);
  };
  useEffect(() => () => {
    if (hoverTimerRef.current !== null) window.clearTimeout(hoverTimerRef.current);
  }, []);
  return (
    <>
      <div className="cardbar">
        {rows.map((row, r) => (
          <div className="cardrow" key={r}>
            {row.map((cid, i) => {
              const slot = i;
              const def = cardDef(cid);
              const cr = p.cards[cid];
              const uiState = cardUiState(game.sim, game.localPlayer, cid);
              const disabled = uiState !== 'available';
              const targeting = game.pending.kind === 'card' && game.pending.cardId === cid;
              const edgeClass = i < 2 ? ' hover-edge-left' : i >= row.length - 2 ? ' hover-edge-right' : '';
              const footer = uiState === 'cooldown'
                ? `${Math.ceil(cr.cooldownRemaining)}s`
                : cr.usesRemaining !== Infinity
                  ? `${cr.usesRemaining}×`
                  : undefined;
              return (
                <div
                  key={cid}
                  className={`hudcardwrap${edgeClass} ${disabled ? 'disabled' : ''} ${targeting ? 'targeting' : ''}`}
                  onMouseEnter={() => startHover(cid)}
                  onMouseLeave={() => cancelHover(cid)}
                >
                  {hover === cid && (
                    <div className="hudcard-hover" aria-hidden="true">
                      <CardFace card={def} variant="hover" disabled={disabled} />
                    </div>
                  )}
                  <CardFace
                    card={def}
                    variant="compact"
                    selected={targeting}
                    hotkey={DECK_HOTKEYS[slot]}
                    onClick={() => !disabled && game.beginCardTargeting(cid)}
                    onHover={(focused) => focused ? startHover(cid) : cancelHover(cid)}
                    footer={footer}
                  />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}

// ---------------------------------------------------------------- gather split (global)

/** Global worker panel — nothing but the Power/Essence allocation slider. */
export function WorkerPanel({ game }: { game: Game }) {
  const p = game.sim.state.players[game.localPlayer];
  const idle = idleWorkerCount(game.sim, game.localPlayer);
  return (
    <div className="panel gatherpanel">
      <div className="paneltitle">Gather Split</div>
      <div className="row">
        <span className="powlabel">⚡ {p.allocationPower}%</span>
        <input
          type="range"
          min={0}
          max={100}
          value={p.allocationPower}
          onChange={(e) => game.dispatch({ kind: 'SetAllocation', player: 0, power: Number(e.target.value) })}
        />
        <span className="esslabel">{100 - p.allocationPower}% ✦</span>
      </div>
      {/* fixed-height slot so the bottom-anchored panel never grows/shrinks when warnings blip */}
      <div className="warnrow">
        {p.noEssenceWarning ? (
          <span className="warnline">⚠ No reachable Essence</span>
        ) : idle > 0 ? (
          <span className="warnline">⚠ {idle} idle worker{idle > 1 ? 's' : ''}</span>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- command bar

const COMMANDS: { key: HLKind; name: string; icon: string; hotkey: string; hint: string }[] = [
  { key: 'attack', name: 'Attack', icon: '⚔', hotkey: 'A', hint: 'Engage a target or fight toward a point.' },
  { key: 'capture', name: 'Capture', icon: '⚑', hotkey: 'C', hint: 'Take a strategic site.' },
  { key: 'defend', name: 'Defend', icon: '🛡', hotkey: 'D', hint: 'Hold an area and engage anything entering it.' },
  { key: 'patrol', name: 'Patrol', icon: '👣', hotkey: 'P', hint: 'Loop between your current position and a chosen point.' },
  { key: 'raid', name: 'Raid', icon: '🔥', hotkey: 'R', hint: 'Hunt enemy workers and disrupt their economy.' },
  { key: 'escort', name: 'Escort', icon: '🤝', hotkey: 'E', hint: 'Form up around and protect a selected boss.' },
  { key: 'withdraw', name: 'Withdraw', icon: '↩', hotkey: 'W', hint: 'Disengage immediately and fall back to safety.' },
];

export function CommandBar({ game }: { game: Game }) {
  if (game.selection.squads.length === 0) return null;
  return (
    <div className="commandbar">
      {COMMANDS.map((c) => (
        <button
          key={c.key}
          className="commandbutton"
          aria-label={`${c.name} command. ${c.hint} Hotkey ${c.hotkey}.`}
          onClick={() => game.beginCommandTargeting(c.key)}
        >
          <span className="commandicon" aria-hidden="true">{c.icon}</span>
          <span className="commandtooltip" role="tooltip">
            <span className="commandtooltiphead">
              <strong>{c.name}</strong>
              <kbd>{c.hotkey}</kbd>
            </span>
            <span>{c.hint}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- worker controls (per building)

/**
 * Worker recruitment block shown when the Crown Bastion or a Site Tower is selected
 * (mockup panel). Left-click +1 Worker queues one; right-click toggles auto-recruit.
 */
function WorkerControls({ game, b }: { game: Game; b: Building }) {
  const p = game.sim.state.players[game.localPlayer];
  const isCore = b.isCore;
  // base-wall bulk build (owner request): the whole ring + central gate raises for one total
  // cost, from the Bastion — never piecemeal
  const baseGroup = game.sim.baseWallGroup(game.localPlayer);
  const baseRuins = isCore
    ? game.sim.state.ruins.filter(
        (r) => r.group === baseGroup && r.buildingId === null && game.sim.state.time >= r.cooldownUntil,
      )
    : [];
  const baseWallCost = baseRuins.reduce((sum, r) => sum + r.cost, 0);
  const auto = isCore ? p.autoWorker : b.workerAuto;
  const queueText = isCore
    ? p.workerQueue.length > 0
      ? `training… ${Math.ceil(p.workerQueue[0].remaining)}s${p.workerQueue.length > 1 ? ` (+${p.workerQueue.length - 1})` : ''}`
      : ''
    : b.workerQueueRemaining !== null
      ? `training… ${Math.ceil(b.workerQueueRemaining)}s`
      : '';
  const queueWorker = () =>
    game.dispatch(isCore ? { kind: 'QueueWorker', player: 0 } : { kind: 'QueueTowerWorker', player: 0, buildingId: b.id });
  const toggleAuto = () =>
    game.dispatch(
      isCore
        ? { kind: 'SetAutoWorker', player: 0, enabled: !p.autoWorker }
        : { kind: 'ToggleTowerWorker', player: 0, buildingId: b.id },
    );
  // per-zone cap (owner feedback): this hub's workers out of its own zone allowance
  const zoneUsed = game.sim.zoneWorkerCount(game.localPlayer, b);
  const zoneCap = game.sim.zoneWorkerCap(game.localPlayer, b);
  const atCap = p.workforceUsed >= p.workforceCap || zoneUsed >= zoneCap;
  return (
    <div className="workerblock">
      <div className="paneltitle small">Workers</div>
      <div className="workerrow">
        <span className="portrait">👷</span>
        <div className="countcol">
          <div className="countbar">
            <div style={{ width: `${Math.min(100, (zoneUsed / Math.max(1, zoneCap)) * 100)}%` }} />
          </div>
          <div className="countline">
            <b>{zoneUsed}</b>/{zoneCap} in this zone · {p.workforceUsed}/{p.workforceCap} total
            <span className="cost">⚡{TUNING.workerCost}</span>
          </div>
        </div>
      </div>
      {queueText && <div className="queueline">{queueText}</div>}
      <div className="row">
        <button
          className="btn small"
          disabled={atCap || p.power < TUNING.workerCost}
          title={`Left-click: train one worker (⚡${TUNING.workerCost}, ${TUNING.workerTrainTime}s) ${isCore ? 'at the Bastion' : 'at this tower'}. Right-click: toggle auto-recruit.`}
          onClick={queueWorker}
          onContextMenu={(e) => {
            e.preventDefault();
            toggleAuto();
          }}
        >
          +1 Worker
        </button>
        <button
          className={`btn small ${auto ? 'toggled' : ''}`}
          title="Keep training workers automatically while below the workforce cap"
          onClick={toggleAuto}
        >
          ⟳ Auto{auto ? ' ✓' : ''}
        </button>
      </div>
      <div className="row">
        <button
          className="btn small wide"
          title={
            b.recallActive
              ? 'This zone\'s workers leave shelter and resume work'
              : 'Shelter only this zone\'s workers in the Core'
          }
          onClick={() =>
            game.dispatch(
              b.recallActive
                ? { kind: 'ResumeWork', player: 0, hubId: b.id }
                : { kind: 'RecallWorkers', player: 0, hubId: b.id },
            )
          }
        >
          {b.recallActive ? '▶ Resume zone (H)' : '🏠 Recall zone (H)'}
        </button>
      </div>
      {isCore && baseRuins.length > 0 && (
        <div className="row">
          <button
            className="btn small wide"
            disabled={p.power < baseWallCost}
            title="Raise the entire base wall — every segment plus the central gate — in one build"
            onClick={() => game.dispatch({ kind: 'BuildBaseWall', player: 0, group: baseGroup })}
          >
            🧱 Build Base Wall (⚡{baseWallCost})
          </button>
        </div>
      )}
      <div className="mutedline">
        Sheltered: {p.shelterOccupants.length}/{p.shelterCapacity}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- upgrade rows

type UpgradeState = 'done' | 'researching' | 'available';

function UpgradeTile({
  game,
  b,
  up,
  state,
  remaining,
  index,
  onInspect,
}: {
  game: Game;
  b: Building;
  up: UpgradeDef;
  state: UpgradeState;
  remaining?: number;
  index: number;
  onInspect: (id: string, tile: HTMLButtonElement) => void;
}) {
  const p = game.sim.state.players[game.localPlayer];
  const affordable = p.power >= up.cost.power && p.essence >= up.cost.essence;
  const blocked = state !== 'available' || !affordable || b.upgradeInProgress !== null;
  return (
    <button
      type="button"
      className={`upgradetile ${state}${blocked ? ' blocked' : ''}`}
      aria-label={`${up.name}, ${state}${state === 'available' && !affordable ? ', insufficient resources' : ''}`}
      aria-disabled={blocked}
      onMouseEnter={(event) => onInspect(up.id, event.currentTarget)}
      onFocus={(event) => onInspect(up.id, event.currentTarget)}
      onClick={(event) => {
        onInspect(up.id, event.currentTarget);
        if (!blocked) {
          game.dispatch({ kind: 'BuyCoreUpgrade', player: 0, buildingId: b.id, upgradeId: up.id });
        }
      }}
    >
      <span className="upicon" aria-hidden="true">{up.icon ?? '⬡'}</span>
      <span className="uptier">{ROMAN[index] ?? index + 1}</span>
      {state === 'done' && <span className="upstate done" aria-hidden="true">✓</span>}
      {state === 'researching' && <span className="upstate researching">{Math.ceil(remaining ?? 0)}s</span>}
    </button>
  );
}

// ---------------------------------------------------------------- Core Annex System

const SOCKET_LABELS: Record<'top' | 'left' | 'bottom', string> = {
  top: '▲ Signature · Military',
  left: '◀ Economy · Workforce',
  bottom: '▼ Expansion · Deployment',
};

/** Live status line(s) + controls for a COMPLETED socketed annex. */
function AnnexStatus({ game, up }: { game: Game; up: UpgradeDef }) {
  const st = game.sim.state;
  const p = st.players[game.localPlayer];
  const annex = p.annex;
  const lines: ReactNode[] = [];

  if (up.spawner) {
    const sp = up.spawner;
    let alive = 0;
    for (const s of st.squads.values()) {
      if (s.owner === game.localPlayer && s.sourceAnnexId === up.id) alive++;
    }
    const label = unitDef(sp.unitId).ui.label;
    if (annex.stored[up.id]) {
      lines.push(
        <div key="stored" style={{ color: '#ffc14d', fontSize: 11.5 }}>
          {label} ready — Command full, held at the annex
        </div>,
      );
    } else if (alive >= sp.maxAlive) {
      lines.push(
        <div key="cap" className="mutedline">
          {label}: {alive}/{sp.maxAlive} active — growth paused
        </div>,
      );
    } else {
      lines.push(
        <div key="next" className="mutedline">
          Next {label} in {Math.ceil(annex.spawnTimers[up.id] ?? sp.period)}s ({alive}/{sp.maxAlive})
        </div>,
      );
    }
  }

  if (up.bloodTithe) {
    const spec = up.bloodTithe;
    if (annex.apotheosis) {
      const cocoon = st.buildings.get(annex.apotheosis.buildingId);
      lines.push(
        <div key="apo" style={{ color: '#ff8f7a', fontSize: 11.5 }}>
          Apotheosis in {Math.ceil(annex.apotheosis.remaining)}s — defend the Cocoon
          {cocoon ? ` (${Math.ceil(cocoon.health)}/${cocoon.maxHealth})` : ''}
        </div>,
      );
    } else if (annex.apotheosisUsed) {
      lines.push(
        <div key="spent" className="mutedline">
          Apotheosis completed — one per match
        </div>,
      );
    } else {
      lines.push(
        <div key="tithe" className="row" style={{ alignItems: 'center', gap: 6 }}>
          <button
            className="btn small"
            onClick={() => game.dispatch({ kind: 'SetTithe', player: 0, active: !annex.titheActive })}
          >
            {annex.titheActive ? '⏸ Pause Blood Tithe' : '🩸 Begin Blood Tithe'}
          </button>
          <span style={{ fontSize: 11.5, color: '#ff8f7a' }}>
            {annex.souls}/{spec.soulsRequired} Souls Offered
          </span>
        </div>,
      );
      if (annex.titheActive) {
        lines.push(
          <div key="timer" className="mutedline">
            Next sacrifice in {Math.ceil(annex.titheTimer)}s (pauses under {spec.minWorkers} workers)
          </div>,
        );
      }
    }
    if (!annex.apotheosisUsed && spec.forms.length > 1) {
      lines.push(
        <div key="forms" className="row" style={{ gap: 4 }}>
          {spec.forms.map((f) => (
            <button
              key={f.form}
              className="btn small"
              style={annex.apotheosisForm === f.form ? { outline: '1px solid #ffd76e' } : undefined}
              title={unitDef(f.unitId).name}
              onClick={() => game.dispatch({ kind: 'ChooseApotheosis', player: 0, form: f.form })}
            >
              {annex.apotheosisForm === f.form ? '◉ ' : '○ '}
              {unitDef(f.unitId).ui.label}
            </button>
          ))}
        </div>,
      );
    }
  }

  if (up.deepRecall) {
    const ready = annex.deepRecallCooldown <= 0;
    lines.push(
      <button
        key="recall"
        className="btn small wide"
        disabled={!ready}
        title={`Up to ${up.deepRecall.maxWorkers} retreating workers burrow into the Core shelter after a ${up.deepRecall.channelSeconds}s channel (damage interrupts).`}
        onClick={() => game.dispatch({ kind: 'DeepRecall', player: 0 })}
      >
        🕳 Deep Recall{ready ? '' : ` (${Math.ceil(annex.deepRecallCooldown)}s)`}
      </button>,
    );
  }

  if (up.workerTransfer) {
    const ready = annex.transferCooldown <= 0;
    const targets = [...st.buildings.values()].filter(
      (t) =>
        t.owner === game.localPlayer &&
        t.state === 'active' &&
        !t.isCore &&
        buildingDef(t.defId).conduit &&
        st.connectedConduits.has(t.id),
    );
    lines.push(
      <div key="transfer" style={{ fontSize: 11.5 }}>
        <div className="mutedline">
          Worker transfer ({p.shelterOccupants.length} sheltered)
          {ready ? '' : ` — ready in ${Math.ceil(annex.transferCooldown)}s`}
        </div>
        {targets.slice(0, 4).map((t) => (
          <button
            key={t.id}
            className="btn small"
            disabled={!ready || p.shelterOccupants.length === 0}
            onClick={() => game.dispatch({ kind: 'TransferWorkers', player: 0, buildingId: t.id })}
          >
            ⇢ {buildingDef(t.defId).ui.label}
          </button>
        ))}
        {targets.length === 0 && <div className="mutedline">No connected Conduits</div>}
      </div>,
    );
  }

  if (up.echo) {
    const charged = annex.echoCharge >= 1;
    lines.push(
      <div key="echo" className="row" style={{ alignItems: 'center', gap: 6 }}>
        <button
          className="btn small"
          onClick={() => game.dispatch({ kind: 'SetEchoArmed', player: 0, armed: !annex.echoArmed })}
        >
          {annex.echoArmed ? '◉ Echo armed' : '○ Echo held'}
        </button>
        <span style={{ fontSize: 11.5, color: charged ? '#9fe8b5' : '#8b94a2' }}>
          {charged ? 'Charge ready' : `Charging ${Math.ceil(up.echo.period - annex.echoProgress)}s`}
        </span>
      </div>,
    );
  }

  return <div className="annexstatus">{lines}</div>;
}

function CoreAnnexes({ game, b, upgrades }: { game: Game; b: Building; upgrades: UpgradeDef[] }) {
  const [inspectedId, setInspectedId] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0, top: 0, maxHeight: 0 });
  const upgradeBlockRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const p = game.sim.state.players[game.localPlayer];
  const ordered = [
    ...(['top', 'left', 'bottom'] as const)
      .map((socket) => upgrades.find((up) => up.socket === socket))
      .filter((up): up is UpgradeDef => Boolean(up)),
    ...upgrades.filter((up) => !up.socket),
  ];
  const stateFor = (up: UpgradeDef): UpgradeState =>
    b.upgradesDone.includes(up.id)
      ? 'done'
      : b.upgradeInProgress?.id === up.id
        ? 'researching'
        : 'available';
  const inspected = ordered.find((up) => up.id === inspectedId) ?? null;
  const inspectedState = inspected ? stateFor(inspected) : null;
  const inspectedAffordable = inspected
    ? p.power >= inspected.cost.power && p.essence >= inspected.cost.essence
    : false;

  const cancelClose = () => {
    if (closeTimerRef.current === null) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => {
      setInspectedId(null);
      closeTimerRef.current = null;
    }, 240);
  };
  const inspect = (id: string, tile: HTMLButtonElement) => {
    cancelClose();
    const tileRect = tile.getBoundingClientRect();
    const panelRect = tile.closest('.selpanel')?.getBoundingClientRect() ?? tileRect;
    const tooltipWidth = 330;
    const viewportGutter = 10;
    let left = panelRect.right + viewportGutter;
    if (left + tooltipWidth > window.innerWidth - viewportGutter) {
      left = Math.max(viewportGutter, panelRect.left - tooltipWidth - viewportGutter);
    }
    const top = Math.max(66, Math.min(tileRect.top - 110, window.innerHeight - 350));
    setTooltipPosition({ left, top, maxHeight: Math.max(180, window.innerHeight - top - viewportGutter) });
    setInspectedId(id);
  };

  useEffect(() => {
    const closeWhenFocusLeaves = (event: FocusEvent) => {
      const target = event.target as Node | null;
      if (upgradeBlockRef.current?.contains(target) || tooltipRef.current?.contains(target)) return;
      setInspectedId(null);
    };
    document.addEventListener('focusin', closeWhenFocusLeaves);
    return () => {
      document.removeEventListener('focusin', closeWhenFocusLeaves);
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  const tooltip = inspected && inspectedState && (
    <div
      ref={tooltipRef}
      id="core-annex-tooltip"
      className="annexdetail annextooltip"
      style={tooltipPosition}
      role="dialog"
      aria-label={`${inspected.name} annex details`}
      aria-live="polite"
      onMouseEnter={cancelClose}
      onMouseLeave={scheduleClose}
    >
      <div className="annexdetailhead">
        <span className="annexdetailicon" aria-hidden="true">{inspected.icon ?? '⬡'}</span>
        <div>
          <strong>{inspected.name}</strong>
          <span>{inspected.socket ? SOCKET_LABELS[inspected.socket] : 'Core Upgrade'}</span>
        </div>
      </div>
      <p>{inspected.rulesText}</p>
      <div className="annexmeta">
        <span>⚡ {inspected.cost.power}</span>
        {inspected.cost.essence > 0 && <span>✦ {inspected.cost.essence}</span>}
        <span>◷ {inspected.buildTime ?? TUNING.upgradeResearchSeconds}s</span>
      </div>
      {inspectedState === 'done' && <div className="annexavailability complete">Built</div>}
      {inspectedState === 'researching' && (
        <div className="annexavailability researching">
          Building — {Math.ceil(b.upgradeInProgress?.remaining ?? 0)}s remaining
        </div>
      )}
      {inspectedState === 'available' && (
        <div className={`annexavailability ${inspectedAffordable && !b.upgradeInProgress ? 'ready' : 'blocked'}`}>
          {!inspectedAffordable
            ? 'Insufficient resources'
            : b.upgradeInProgress
              ? 'Another annex is under construction'
              : 'Ready — click the square to construct'}
        </div>
      )}
      {inspectedState === 'done' && <AnnexStatus game={game} up={inspected} />}
      <div className="annexrule">All three sockets can be built, one at a time.</div>
    </div>
  );

  return (
    <>
      <div ref={upgradeBlockRef} className="upgradeblock">
        <div className="paneltitle small">Core Annexes</div>
        <div
          className="upgradegrid"
          role="group"
          aria-label="Core Annexes"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {ordered.map((up, index) => (
            <UpgradeTile
              key={up.id}
              game={game}
              b={b}
              up={up}
              state={stateFor(up)}
              remaining={b.upgradeInProgress?.id === up.id ? b.upgradeInProgress.remaining : undefined}
              index={index}
              onInspect={inspect}
            />
          ))}
        </div>
        <div className="annexprompt">Hover or focus an annex for details.</div>
      </div>
      {tooltip && createPortal(tooltip, document.body)}
    </>
  );
}

// ---------------------------------------------------------------- selection panel

export function SelectionPanel({ game }: { game: Game }) {
  const st = game.sim.state;
  const sel = game.selection;
  if (sel.buildingId !== null) {
    const b = st.buildings.get(sel.buildingId);
    if (!b) return null;
    const bd = buildingDef(b.defId);
    const p = st.players[game.localPlayer];
    return (
      <div className="panel selpanel">
        {b.isCore ? (
          <div className="coreselectionhead">
            <span className="coreselectionicon" aria-hidden="true">🏰</span>
            <div className="coreselectionstats">
              <h4>
                {bd.name} {b.state !== 'active' ? `— ${b.state}` : ''}
              </h4>
              <div className="hpbar">
                <div style={{ width: `${(b.health / b.maxHealth) * 100}%` }} />
              </div>
              <div className="mutedline">
                {Math.ceil(b.health)}/{b.maxHealth}
                {b.tempHealth > 0 && ` (+${Math.ceil(b.tempHealth)} temp)`}
              </div>
            </div>
          </div>
        ) : (
          <>
            <h4>
              {bd.name} {b.state !== 'active' ? `— ${b.state}` : ''}
            </h4>
            <div className="hpbar">
              <div style={{ width: `${(b.health / b.maxHealth) * 100}%` }} />
            </div>
            <div className="mutedline">
              {Math.ceil(b.health)}/{b.maxHealth}
              {b.tempHealth > 0 && ` (+${Math.ceil(b.tempHealth)} temp)`}
            </div>
          </>
        )}
        {(b.state === 'blueprint' || b.state === 'constructing') && (
          <div style={{ color: '#ffc14d' }}>Construction {(b.buildProgress * 100).toFixed(0)}%</div>
        )}

        {bd.signatureText && <div className="mutedline" style={{ marginBottom: 5 }}>{bd.signatureText}</div>}

        {bd.cruelProduction && b.state === 'active' && (
          <button
            className="btn small wide"
            disabled={b.queue.length === 0 || p.workforceUsed <= 5}
            title="Sacrifice the nearest worker to remove 25 seconds from the current queue and grant the Gore Engine Blood Armor. Disabled at five or fewer workers."
            onClick={() => game.dispatch({ kind: 'ToggleBuildingMode', player: 0, buildingId: b.id })}
          >
            🩸 Cruel Production
          </button>
        )}
        {bd.traits?.some((trait) => trait.kind === 'remoteDeployment' && trait.requiresMode) && b.state === 'active' && (
          <button
            className={`btn small wide ${b.modeActive ? 'toggled' : ''}`}
            title="Deploy completed squads from the connected Conduit nearest this building's rally point."
            onClick={() => game.dispatch({ kind: 'ToggleBuildingMode', player: 0, buildingId: b.id })}
          >
            {b.modeActive ? '◉ Remote Muster' : '○ Local Muster'}
          </button>
        )}
        {bd.traits?.some((trait) => trait.kind === 'doctrine') && b.upgradeDone && b.state === 'active' && (
          <button
            className={`btn small wide ${b.modeActive ? 'toggled' : ''}`}
            title="Switch auto-production doctrine between Shield Legion and Pike Wardens."
            onClick={() => game.dispatch({ kind: 'ToggleBuildingMode', player: 0, buildingId: b.id })}
          >
            Doctrine: {b.modeActive ? 'Anti-Large' : 'Defensive'}
          </button>
        )}
        {bd.traits?.some((trait) => trait.kind === 'focusBeam') && b.upgradeDone && b.state === 'active' && (
          <button
            className={`btn small wide ${b.modeActive ? 'toggled' : ''}`}
            title="Switch between the full focusing beam and two weaker beams for swarm defense."
            onClick={() => game.dispatch({ kind: 'ToggleBuildingMode', player: 0, buildingId: b.id })}
          >
            {b.modeActive ? 'Split Prism: Two Beams' : 'Split Prism: Focus Beam'}
          </button>
        )}

        {(b.isCore || bd.producesWorkers) && b.state === 'active' && <WorkerControls game={game} b={b} />}

        {b.isCore && bd.upgrades && b.state === 'active' && (
          <CoreAnnexes game={game} b={b} upgrades={bd.upgrades} />
        )}

        {bd.producesWorkers && b.state === 'active' && (
          <div className="mutedline">
            Resource drop-off for nearby workers. Losing this tower loses the site.
          </div>
        )}
        {bd.kind === 'wall' && b.gateSpan > 0 && b.state === 'active' && (
          <div style={{ margin: '4px 0', fontSize: 11.5 }}>
            Gate:{' '}
            <span style={{ color: b.gateNavOpen ? '#72ef8f' : b.gateOpenFraction > 0 ? '#ffc14d' : '#ff7168' }}>
              {b.gateNavOpen
                ? 'Open'
                : b.gateOpen
                  ? `Opening (${Math.round(b.gateOpenFraction * 100)}%)`
                  : b.gateOpenFraction > 0
                    ? `Closing (${Math.round(b.gateOpenFraction * 100)}%)`
                    : 'Closed'}
            </span>
            <div className="mutedline">Opens automatically for friendly traffic and closes once the passage is clear.</div>
          </div>
        )}
        {(bd.mountSlots ?? 0) > 0 && b.state === 'active' && (
          <div style={{ margin: '4px 0', fontSize: 11.5 }}>
            Garrison: {[...game.sim.state.squads.values()].filter((s) => s.mountedOnWall === b.id).length}/
            {Math.min(2, bd.mountSlots ?? 0)}
            <div className="mutedline">
              Select a ranged squad and right-click the wall to mount (+25% range, protected; max 2).
              <br />
              Right-click a selected mounted squad to dismount it.
            </div>
          </div>
        )}
        {bd.upgrade && b.state === 'active' && !b.upgradeDone && b.upgradeRemaining <= 0 && (
          <button
            className="btn small wide"
            disabled={p.power < bd.upgrade.cost.power || p.essence < bd.upgrade.cost.essence}
            title={bd.upgrade.rulesText}
            onClick={() => game.dispatch({ kind: 'BuyUpgrade', player: 0, buildingId: b.id })}
          >
            {bd.upgrade.name} (⚡{bd.upgrade.cost.power}
            {bd.upgrade.cost.essence > 0 ? ` ✦${bd.upgrade.cost.essence}` : ''})
          </button>
        )}
        {b.upgradeRemaining > 0 && <div style={{ color: '#ffc14d' }}>Researching… {Math.ceil(b.upgradeRemaining)}s</div>}
        {b.upgradeDone && <div style={{ color: '#9fe8b5' }}>✔ {bd.upgrade?.name}</div>}
        {bd.roster && b.state === 'active' && (
          <>
            <div className="queue">
              {b.queue.map((q, i) => (
                <div
                  key={i}
                  className="qslot"
                  title="Click to cancel (full refund)"
                  onClick={() => game.dispatch({ kind: 'CancelQueueItem', player: 0, buildingId: b.id, index: i })}
                >
                  {unitDef(q.unitId).ui.label}
                  {i === 0 ? ` ${Math.ceil(q.remaining)}s` : ''}
                </div>
              ))}
              {b.queue.length === 0 && <span className="mutedline">queue empty</span>}
            </div>
            <div>
              {bd.roster.entries.map((e) => {
                const ud = unitDef(e.unitId);
                const denial = game.sim.unitQueueDenial(b, e.unitId);
                const blocked = denial !== null;
                const activeAuto = Boolean(b.autoProduce[e.unitId]);
                const missing: Record<string, string> = {
                  wrongAscension: `Requires Ascension ${ROMAN[e.ascension - 1]} (currently ${ROMAN[p.ascensionPlayable - 1]})`,
                  insufficientPower: `Missing ${Math.max(0, Math.ceil(e.cost.power - p.power))} Power (need ${e.cost.power})`,
                  insufficientEssence: `Missing ${Math.max(0, Math.ceil(e.cost.essence - p.essence))} Essence (need ${e.cost.essence})`,
                  insufficientCommand: `Missing ${Math.max(0, p.commandUsed + (e.cost.command ?? 0) - p.commandCap)} Command capacity`,
                  workforceFull: `Workforce full (${p.workforceUsed}/${p.workforceCap})`,
                  queueFull: `Production queue full (${b.queue.length}/${bd.roster?.queueSize ?? 5})`,
                  activeLimit: `${ud.name} active-unit limit reached`,
                  invalidUnit: 'This unit is unavailable',
                };
                return (
                  <button
                    type="button"
                    key={e.unitId}
                    className={`rosterbtn ${activeAuto ? 'auto' : ''} ${blocked ? 'unaffordable' : ''}`}
                    aria-disabled={blocked}
                    title={blocked ? `${missing[denial!] ?? denial}. ${activeAuto ? 'Right-click can still disable active auto-production.' : 'Auto-production cannot be enabled until this is resolved.'}` : `Left-click: queue (Power ${e.cost.power}${e.cost.essence ? `, Essence ${e.cost.essence}` : ''}${e.cost.command ? `, Command ${e.cost.command}` : ''}, ${e.trainTime}s). Right-click: toggle auto-production.`}
                    onClick={() => !blocked && game.dispatch({ kind: 'QueueUnit', player: 0, buildingId: b.id, unitId: e.unitId })}
                    onContextMenu={(ev) => {
                      ev.preventDefault();
                      if (!blocked || activeAuto) game.dispatch({ kind: 'ToggleAutoProduce', player: 0, buildingId: b.id, unitId: e.unitId });
                    }}
                  >
                    <span>{ud.name}</span>
                    <span style={{ color: '#7fd0ff' }}>
                      ⚡{e.cost.power} {e.cost.command ? `⚔${e.cost.command}` : ''} {ROMAN[e.ascension - 1]}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mutedline">Right-click ground with building selected: set rally</div>
          </>
        )}
        {b.isCore && b.state === 'active' && (
          <div className="mutedline">Right-click ground to set the Core annex rally flag ({Math.round(b.rally.x)}, {Math.round(b.rally.y)}). Recurring military spawns defend it.</div>
        )}
        {(b.state === 'blueprint' || b.state === 'constructing') && (
          <button className="btn small" onClick={() => game.dispatch({ kind: 'CancelConstruction', player: 0, buildingId: b.id })}>
            Cancel (75% refund)
          </button>
        )}
      </div>
    );
  }

  if (sel.squads.length > 0) {
    const squads = sel.squads.map((id) => st.squads.get(id)).filter((s) => !!s);
    return (
      <div className="panel selpanel">
        <h4>
          {squads.length === 1 ? unitDef(squads[0]!.defId).name : `${squads.length} squads`}
        </h4>
        {squads.slice(0, 8).map((s) => {
          const d = unitDef(s!.defId);
          return (
            <div className="squadrow" key={s!.id}>
              <span style={{ minWidth: 64, fontSize: 11.5 }}>{d.ui.label}</span>
              <div className="hpbar" style={{ flex: 1 }}>
                <div style={{ width: `${(s!.health / s!.maxHealth) * 100}%` }} />
              </div>
              <span style={{ fontSize: 10, color: '#8b94a2' }}>
                {squadMembers(s!)}/{d.squadMemberCount}
              </span>
              <span className="cmd">
                {s!.command.kind}
                {s!.command.auto ? ' (auto)' : ''}
              </span>
            </div>
          );
        })}
        {squads.length === 1 && squads[0]!.isBoss && (
          <div style={{ fontSize: 11, color: '#ffd76e', marginTop: 4 }}>
            Boss — escorts: {[...st.squads.values()].filter((x) => x.escortOf === squads[0]!.id).length}
            {' '}(select squads → E → click boss)
          </div>
        )}
        {squads.length === 1 && unitDef(squads[0]!.defId).deploysInto && (() => {
          const s = squads[0]!;
          const dep = unitDef(s.defId).deploysInto!;
          const inTerritory = game.sim.inFriendlyTerritory(s.pos, game.localPlayer);
          return (
            <div style={{ marginTop: 4 }}>
              <button
                className="btn small wide"
                disabled={!inTerritory}
                title={`Unfolds here into a free ${buildingDef(dep.buildingId).name} over ${dep.assembleSeconds}s. No workers needed.`}
                onClick={() => game.dispatch({ kind: 'DeployCarrier', player: 0, squadId: s.id })}
              >
                E: {dep.label}
              </button>
              {!inTerritory && <div className="mutedline">Move into controlled territory to deploy.</div>}
            </div>
          );
        })()}
        {squads.length === 1 && unitDef(squads[0]!.defId).rootable && (() => {
          const s = squads[0]!;
          return (
            <div style={{ marginTop: 4 }}>
              <button
                className="btn small wide"
                disabled={s.uprootRemaining > 0}
                title="Rooted: bark armor, thorn volley, and an economy aura (heals workers, +gather, +retreat speed). Uprooting takes 8s and suppresses the aura for 20s."
                onClick={() => game.dispatch({ kind: 'ToggleRoot', player: 0, squadId: s.id })}
              >
                {s.uprootRemaining > 0
                  ? `Uprooting… ${Math.ceil(s.uprootRemaining)}s`
                  : s.treeRooted
                    ? 'E: Uproot'
                    : 'E: Take Root'}
              </button>
              {s.treeRooted && s.uprootRemaining <= 0 && (
                <div className="mutedline">Rooted — guarding the ground it stands on.</div>
              )}
              {s.auraSuppressedRemaining > 0 && (
                <div className="mutedline">Economy aura recovering ({Math.ceil(s.auraSuppressedRemaining)}s)</div>
              )}
            </div>
          );
        })()}
        {squads.length === 1 && (
          <div className="mutedline">
            {unitDef(squads[0]!.defId).abilities.length > 0 ? 'Automatic abilities fire on their own — watch for the flash.' : ''}
          </div>
        )}
      </div>
    );
  }

  if (sel.workers.length > 0) {
    return (
      <div className="panel selpanel">
        <h4>{sel.workers.length} worker{sel.workers.length > 1 ? 's' : ''}</h4>
        <div className="mutedline">
          Right-click a damaged building: repair. Right-click the Core: shelter.
        </div>
      </div>
    );
  }

  // read-only inspection targets (allied/enemy entities, deposits, wall foundations)
  if (sel.inspect !== null) {
    const insp = sel.inspect;
    if (insp.kind === 'enemySquad') {
      const s = st.squads.get(insp.id);
      if (!s) return null;
      const d = unitDef(s.defId);
      const allied = game.sim.areAllies(s.owner, game.localPlayer);
      return (
        <div className="panel selpanel">
          <h4 style={{ color: allied ? '#7fd0ff' : '#ff8f7a' }}>{d.name} — {allied ? 'ally' : 'enemy'}</h4>
          <div className="hpbar">
            <div style={{ width: `${(s.health / s.maxHealth) * 100}%` }} />
          </div>
          <div className="mutedline">
            {Math.ceil(s.health)}/{s.maxHealth} · {squadMembers(s)}/{d.squadMemberCount} members
          </div>
        </div>
      );
    }
    if (insp.kind === 'enemyBuilding') {
      const b = st.buildings.get(insp.id);
      if (!b) return null;
      const bd = buildingDef(b.defId);
      const allied = game.sim.areAllies(b.owner, game.localPlayer);
      return (
        <div className="panel selpanel">
          <h4 style={{ color: allied ? '#7fd0ff' : '#ff8f7a' }}>
            {bd.name} — {allied ? 'ally' : 'enemy'}{b.state !== 'active' ? ` (${b.state})` : ''}
          </h4>
          <div className="hpbar">
            <div style={{ width: `${(b.health / b.maxHealth) * 100}%` }} />
          </div>
          <div className="mutedline">
            {Math.ceil(b.health)}/{b.maxHealth}
          </div>
          {(b.state === 'blueprint' || b.state === 'constructing') && (
            <div style={{ color: '#ffc14d' }}>Under construction — {(b.buildProgress * 100).toFixed(0)}%</div>
          )}
        </div>
      );
    }
    if (insp.kind === 'deposit') {
      const dep = st.deposits.get(insp.id);
      if (!dep) return null;
      const label = dep.type === 'power' ? '⚡ Power Deposit' : '✦ Essence Deposit';
      const color = dep.type === 'power' ? '#7fd0ff' : '#d0a6ff';
      return (
        <div className="panel selpanel">
          <h4 style={{ color }}>{label}</h4>
          <div className="hpbar">
            <div style={{ width: `${(dep.amount / dep.initialAmount) * 100}%`, background: color }} />
          </div>
          <div className="mutedline">
            {Math.floor(dep.amount)} / {dep.initialAmount} remaining · {dep.gatherSlots} gather slots
          </div>
          {dep.amount <= 0 && <div style={{ color: '#ffc14d' }}>Depleted</div>}
        </div>
      );
    }
    // ruin — unbuilt wall foundation
    return <RuinPanel game={game} ruinId={insp.id} />;
  }
  return null;
}

/** Wall foundation (ruin) info — the Build Wall confirm flow lives here. */
function RuinPanel({ game, ruinId }: { game: Game; ruinId: EntId }) {
  const st = game.sim.state;
  const ruin = st.ruins.find((r) => r.id === ruinId);
  if (!ruin || ruin.buildingId !== null) return null;
  const p = st.players[game.localPlayer];
  // base-ring foundations raise together from the Bastion, not one at a time (owner request)
  if (ruin.group !== null) {
    return (
      <div className="panel selpanel">
        <h4>Base Wall Foundation</h4>
        <div className="mutedline">
          Part of your base ring. Select your Bastion and use <b>Build Base Wall</b> to raise the
          whole wall — every segment plus the central gate — in one build.
        </div>
      </div>
    );
  }
  const cooldownLeft = Math.max(0, ruin.cooldownUntil - st.time);
  const squadNear = [...st.squads.values()].some(
    (s) => s.owner === game.localPlayer && dist(s.pos, ruin.pos) <= 9,
  );
  const affordable = p.power >= ruin.cost;
  const reason =
    cooldownLeft > 0
      ? `Rubble settling — rebuild in ${Math.ceil(cooldownLeft)}s`
      : !squadNear
        ? 'Move a squad next to the foundation to build'
        : !affordable
          ? 'Not enough Power'
          : null;
  return (
    <div className="panel selpanel">
      <h4>Wall Foundation</h4>
      <div className="mutedline">
        Tumbled stone — raise a castle wall here. Builds slowly and is fragile until finished;
        your nearby troops open its shared gate, which either army can cross until it safely closes.
      </div>
      <button
        className="btn small wide"
        disabled={reason !== null}
        onClick={() => game.dispatch({ kind: 'ClaimRuin', player: 0, ruinId: ruin.id })}
      >
        🧱 Build Wall (⚡{ruin.cost})
      </button>
      {reason !== null && <div className="warnline">{reason}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- minimap

export function Minimap({ game }: { game: Game }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const W = 200;
  const H = Math.round((200 * game.sim.map.height) / game.sim.map.width);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // The canvas backing store was sized in CSS pixels, so the minimap was rendered at 1x and
    // upscaled on every HiDPI display. Size the buffer by devicePixelRatio and scale the context
    // once, so all the drawing below can keep working in CSS-pixel coordinates.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    let raf = 0;
    let last = 0;
    const draw = (now: number) => {
      raf = requestAnimationFrame(draw);
      if (now - last < 83) return; // ~12 Hz (§25.2)
      last = now;
      const st = game.sim.state;
      const sx = W / game.sim.map.width;
      const sy = H / game.sim.map.height;
      ctx.fillStyle = '#131a13';
      ctx.fillRect(0, 0, W, H);
      // rocks
      ctx.fillStyle = '#39404a';
      for (const r of game.sim.map.rocks) {
        ctx.beginPath();
        ctx.ellipse(r.pos.x * sx, r.pos.y * sy, (r.w / 2) * sx, (r.h / 2) * sy, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      // deposits
      for (const d of st.deposits.values()) {
        ctx.fillStyle = d.type === 'power' ? '#51b7e8' : '#b07ce8';
        ctx.fillRect(d.pos.x * sx - 1.5, d.pos.y * sy - 1.5, 3, 3);
      }
      // sites (diamonds)
      for (const s of st.sites) {
        ctx.fillStyle = s.owner === -1 ? (s.contested ? '#ffc14d' : '#9aa0a8') : PLAYER_COLORS[s.owner];
        const x = s.pos.x * sx;
        const y = s.pos.y * sy;
        ctx.beginPath();
        ctx.moveTo(x, y - 5);
        ctx.lineTo(x + 5, y);
        ctx.lineTo(x, y + 5);
        ctx.lineTo(x - 5, y);
        ctx.closePath();
        ctx.fill();
      }
      // buildings (squares)
      for (const b of st.buildings.values()) {
        ctx.fillStyle = PLAYER_COLORS[b.owner];
        const size = b.isCore ? 7 : 4;
        ctx.fillRect(b.pos.x * sx - size / 2, b.pos.y * sy - size / 2, size, size);
      }
      // squads (dots), workers (small dots)
      for (const s of st.squads.values()) {
        ctx.fillStyle = PLAYER_COLORS[s.owner];
        const r = s.isBoss ? 3.4 : 2.2;
        ctx.beginPath();
        ctx.arc(s.pos.x * sx, s.pos.y * sy, r, 0, Math.PI * 2);
        ctx.fill();
      }
      for (const w of st.workers.values()) {
        if (w.state === 'sheltered') continue;
        ctx.fillStyle = PLAYER_COLORS[w.owner];
        ctx.fillRect(w.pos.x * sx - 1, w.pos.y * sy - 1, 2, 2);
      }
      // Camera view: the actual ground-plane footprint of the viewport, unprojected from the
      // four screen corners. This was a fixed 18x12 rectangle around camTarget, which never
      // matched the real view at any zoom — and zoom-dependent pitch makes a fixed box wronger
      // still, since the footprint is now a trapezoid that changes shape as you zoom.
      const corners = game.renderer.viewportGroundQuad();
      if (corners) {
        ctx.strokeStyle = '#e6e9ee';
        ctx.lineWidth = 1;
        ctx.beginPath();
        corners.forEach((corner, index) => {
          const x = corner.x * sx;
          const y = corner.y * sy;
          if (index === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.closePath();
        ctx.stroke();
      }
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [game, H]);

  const players = game.sim.state.players;
  return (
    <div className="minimapwrap">
      <span className="compass">N</span>
      <div className="corebars">
        {players.map((player) => {
          const core = game.sim.coreAlive(player.id);
          const relation = player.id === game.localPlayer ? 'Your' : game.sim.areAllies(player.id, game.localPlayer) ? 'Allied' : 'Enemy';
          return (
            <div className={`corebarrow player-${player.id}`} key={player.id} title={core ? `${relation} ${FACTIONS[player.faction].name} Core: ${Math.ceil(core.health)} / ${core.maxHealth}` : `${relation} Core is destroyed`}>
              <span className="mark" style={{ color: PLAYER_COLORS[player.id] }}>{FACTIONS[player.faction].mark}</span>
              <span className={`corebar ${game.sim.areHostile(player.id, game.localPlayer) ? 'enemy' : ''}`}>
                <div style={{ width: `${core ? (core.health / core.maxHealth) * 100 : 0}%`, backgroundColor: PLAYER_COLORS[player.id] }} />
              </span>
            </div>
          );
        })}
      </div>
      <canvas
        ref={ref}
        className="minimap"
        // Intentionally no width/height props: the effect owns the backing store so it can size
        // it by devicePixelRatio. Two owners would fight over the attribute.
        onMouseDown={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / W) * game.sim.map.width;
          const y = ((e.clientY - rect.top) / H) * game.sim.map.height;
          game.moveCameraTo({ x, y });
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------- alerts / toasts / overlays

export function Alerts({ game }: { game: Game }) {
  return (
    <div className="alerts">
      {game.alerts.map((a) => (
        <div key={a.id} className="alert" onClick={() => a.pos && game.moveCameraTo(a.pos)}>
          {a.text}
        </div>
      ))}
    </div>
  );
}

/** Live arrival timers for the local player's heroes and globally announced enemy heroes. */
export function BossSummonCountdown({ game }: { game: Game }) {
  const pending = game.sim.state.pendingSummons.filter((summon) => {
    const extras = defs.bossExtras[summon.defId];
    return game.sim.areAllies(summon.owner, game.localPlayer) || extras?.announcement === 'global';
  });
  if (pending.length === 0) return null;

  return (
    <div className="bosscountdowns" aria-live="polite">
      {pending.map((summon) => (
        <div
          key={`${summon.owner}-${summon.defId}-${summon.pos.x}-${summon.pos.y}`}
          className="bosscountdown"
        >
          <b>{summon.owner === game.localPlayer ? 'YOUR' : game.sim.areAllies(summon.owner, game.localPlayer) ? 'ALLY' : 'ENEMY'} {unitDef(summon.defId).name}</b>
          <span>ARRIVING IN {Math.ceil(summon.remaining)}s</span>
        </div>
      ))}
    </div>
  );
}

export function DenyToast({ game }: { game: Game }) {
  const now = performance.now() / 1000;
  if (now - game.lastDenyAt > 2.5 || !game.lastDenyReason) return null;
  const nice: Record<string, string> = {
    noUses: 'No uses remaining',
    cooldown: 'On cooldown',
    activeLimit: 'Active-copy limit reached',
    wrongAscension: 'Requires a higher Ascension — control more sites',
    insufficientPower: 'Not enough Power',
    insufficientEssence: 'Not enough Essence',
    insufficientCommand: 'Command cap reached',
    noLocation: 'No valid summon location',
    invalidTarget: 'Invalid target',
  };
  return <div className="denytoast">{nice[game.lastDenyReason] ?? game.lastDenyReason}</div>;
}

export function PendingHint({ game }: { game: Game }) {
  if (game.pending.kind === 'card') {
    const def = cardDef(game.pending.cardId);
    const where =
      def.category === 'spell'
        ? spellDef(def.subjectId).targeting.shape === 'entity'
          ? 'click a target'
          : 'click target area'
        : def.category === 'building' || def.category === 'trap'
          ? 'click a build location (green = valid)'
          : 'click a summon point at your Core or a controlled site';
    return (
      <div className="pendinghint">
        {def.name}: {where} — Esc cancels
      </div>
    );
  }
  if (game.pending.kind === 'command') {
    return <div className="pendinghint">{game.pending.command}: click the target — Esc cancels</div>;
  }
  return null;
}

export function BoxSelect({ game }: { game: Game }) {
  if (!game.boxStart || !game.boxCurrent) return null;
  const a = game.boxStart;
  const b = game.boxCurrent;
  const style = {
    left: Math.min(a.x, b.x),
    top: Math.min(a.y, b.y),
    width: Math.abs(a.x - b.x),
    height: Math.abs(a.y - b.y),
  };
  return <div className="boxsel" style={style} />;
}

/** Hidden UAT cheat menu — completely invisible until F8 is pressed (owner feedback). */
export function CheatPanel({ game }: { game: Game }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'F8') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  if (!open) return null;
  return (
    <div className="cheat">
      <span className="cheattitle">Cheats</span>
      <button onClick={() => game.cheat(500, 0)}>+500⚡</button>
      <button onClick={() => game.cheat(0, 200)}>+200✦</button>
      <button onClick={() => game.cheatAscension()}>Asc IV</button>
      <button onClick={() => game.epicBattle()} title="Stage a huge two-faction battle with live armies and fallen bodies">
        Epic Battle
      </button>
      <button
        title="Seed 6 corpse marks at your Core (Ashen staging)"
        onClick={() => game.dispatch({ kind: 'CheatCorpse', player: 0, count: 6 })}
      >
        +6💀
      </button>
      <button
        title="Complete your channeling rituals instantly"
        onClick={() => game.dispatch({ kind: 'CheatFinishRituals', player: 0 })}
      >
        Ritual✓
      </button>
      <button onClick={() => setOpen(false)} title="Close (F8)">
        ×
      </button>
    </div>
  );
}

export function CountdownBanner({ game }: { game: Game }) {
  const st = game.sim.state;
  if (st.phase === 'countdown') {
    return <div className="banner">Destroy the enemy Core — {Math.ceil(st.countdownRemaining)}</div>;
  }
  if (st.paused && st.phase === 'running') {
    return <div className="banner">PAUSED — Space to resume (commands still work)</div>;
  }
  return null;
}

export function ResultsOverlay({ game, onRestart, onMenu }: { game: Game; onRestart: () => void; onMenu: () => void }) {
  const st = game.sim.state;
  if (st.phase !== 'ended') return null;
  const abandoned = st.winningTeam === -1;
  const localTeam = st.players[game.localPlayer].team;
  const won = st.winningTeam === localTeam;
  const totals = (team: 0 | 1) => st.players.filter((player) => player.team === team).reduce((sum, player) => ({
    unitsBuilt: sum.unitsBuilt + player.stats.unitsBuilt,
    unitsLost: sum.unitsLost + player.stats.unitsLost,
    sitesCaptured: sum.sitesCaptured + player.stats.sitesCaptured,
    coreDamageDealt: sum.coreDamageDealt + player.stats.coreDamageDealt,
    cardsPlayed: sum.cardsPlayed + player.stats.cardsPlayed,
    powerGathered: sum.powerGathered + player.stats.powerGathered,
  }), { unitsBuilt: 0, unitsLost: 0, sitesCaptured: 0, coreDamageDealt: 0, cardsPlayed: 0, powerGathered: 0 });
  const p = totals(localTeam);
  const e = totals(localTeam === 0 ? 1 : 0);

  // Paired bars instead of "Units built: 12 vs 8". Each row is normalised to the larger of the
  // two values, so which side led a category is readable at a glance rather than by comparing
  // two numbers. Values keep tabular numerals so the columns line up.
  const STATS: { label: string; mine: number; theirs: number; round?: boolean }[] = [
    { label: 'Units built', mine: p.unitsBuilt, theirs: e.unitsBuilt },
    { label: 'Units lost', mine: p.unitsLost, theirs: e.unitsLost },
    { label: 'Sites captured', mine: p.sitesCaptured, theirs: e.sitesCaptured },
    { label: 'Core damage', mine: p.coreDamageDealt, theirs: e.coreDamageDealt, round: true },
    { label: 'Cards played', mine: p.cardsPlayed, theirs: e.cardsPlayed },
    { label: 'Power gathered', mine: p.powerGathered, theirs: e.powerGathered, round: true },
  ];

  // A derived highlight, from stats the sim already records — no new tracking.
  const highlight = (() => {
    if (p.coreDamageDealt > e.coreDamageDealt * 1.5) return 'Overwhelming pressure on the enemy Core.';
    if (p.sitesCaptured > e.sitesCaptured) return 'You controlled the map.';
    if (p.unitsLost < e.unitsLost && p.unitsLost > 0) return 'You traded efficiently.';
    if (e.unitsLost < p.unitsLost) return 'Their army traded better than yours.';
    return null;
  })();

  const outcome = abandoned ? 'abandoned' : won ? 'victory' : 'defeat';
  const localFaction = FACTIONS[st.players[game.localPlayer].faction];

  return (
    <div className={`resultsoverlay glass outcome-${outcome}`}>
      <div className="resultspanel">
        <header className="resultshead">
          <span className="resultscrest" aria-hidden="true">{localFaction.mark}</span>
          <h2>{abandoned ? 'Match Abandoned' : won ? 'Victory' : 'Defeat'}</h2>
          <span className="resultssub">
            {localFaction.name} · {fmtTime(st.time)}
          </span>
        </header>

        {highlight && <p className="resultshighlight">{highlight}</p>}

        <div className="resultsstats">
          <div className="resultslegend">
            <span className="mine">Your team</span>
            <span className="theirs">Opponents</span>
          </div>
          {STATS.map((stat) => {
            const max = Math.max(stat.mine, stat.theirs, 1);
            const fmt = (v: number) => (stat.round ? Math.round(v) : v).toLocaleString();
            return (
              <div className="resultsrow" key={stat.label}>
                <span className="resultsrow-label">{stat.label}</span>
                <span className="resultsrow-bars">
                  <span className="resultsbar mine">
                    <i style={{ width: `${(stat.mine / max) * 100}%` }} />
                    <b className="resultstat-value">{fmt(stat.mine)}</b>
                  </span>
                  <span className="resultsbar theirs">
                    <i style={{ width: `${(stat.theirs / max) * 100}%` }} />
                    <b className="resultstat-value">{fmt(stat.theirs)}</b>
                  </span>
                </span>
              </div>
            );
          })}
        </div>

        <footer className="resultsactions">
          <button className="btn" onClick={onMenu}>Main menu</button>
          <button className="btn primary" onClick={onRestart} autoFocus>
            {game.onlineRuntime ? 'New online match' : 'Play again'}
          </button>
        </footer>
      </div>
    </div>
  );
}
